import { environment } from '../../../../environments/environment';

/**
 * Base origin for ordering API calls.
 * - Local dev (environment.development.ts): an absolute URL like https://localhost:7214 — the
 *   browser calls the .NET API directly (CORS is open on the API).
 * - Deployed (SSR): leave apiBaseUrl '' so calls are relative ('/api/...') and the Express
 *   server in server.ts proxies them to the configured backend (API_HOST).
 */
export const API_BASE = (environment.apiBaseUrl ?? '').replace(/\/+$/, '');

/**
 * Store-search scope for THIS storefront deployment. Empty ⇒ search stores across all tenants
 * (the orderYaa marketplace). Set to a tenant GUID (e.g. a white-label operator's tenant) to show
 * only that tenant's stores — sent to the API as ?tenantId on GET /api/public/stores/nearby.
 */
export const SEARCH_IN_STORES_TENANT_ID = (environment.searchInStoresTenantId ?? '').trim();

/**
 * Platform company (marketplace operator) for this deployment — REQUIRED, no server fallback.
 * Sent with rider registration (the operational rider is created under it at approval) and with
 * every checkout order (dispatch matches order.PlatformCompanyId == rider.CompanyId). The API
 * rejects rider sign-ups and orders when it's missing or not a real active company, so an empty
 * value here disables ordering and rider registration for the whole deployment.
 */
export const PLATFORM_COMPANY = {
  companyId: (environment.platformCompanyId ?? '').trim(),
  branchId: (environment.platformBranchId ?? '').trim(),
  businessUnitId: (environment.platformBusinessUnitId ?? '').trim(),
};

/**
 * Resolve an image URL for display. Absolute URLs (signed blob / CDN) pass through; relative paths
 * like "/images/products/x.jpg" are served by the API host, so they get the API base prefixed.
 */
export function resolveImageUrl(url?: string | null): string | null {
  if (!url) return null;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) return url;
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}
